import React, { useState, useRef } from "react";
import { Friend } from "../../types";
import { formatDateTime, getUserInitial } from "../../utils/formatting";
import { ToggleSwitch } from "../Settings/ToggleSwitch";

interface FriendListItemProps {
  friend: Friend;
  isPending?: boolean; // True if this is a pending outgoing request
  onRemove: (friend: Friend) => void;
  onToggleShareActivities: (friend: Friend) => void;
  onToggleShareLocation: (friend: Friend) => void;
  onToggleShareStatus: (friend: Friend) => void;
  onToggleShareRoutes: (friend: Friend) => void;
  onUpdateMapSettings?: (
    friend: Friend,
    mapIcon: string,
    mapColor: string,
  ) => void;
}

export const FriendListItem = React.memo(
  ({
    friend,
    isPending = false,
    onRemove,
    onToggleShareActivities,
    onToggleShareLocation,
    onToggleShareStatus,
    onToggleShareRoutes,
    onUpdateMapSettings,
  }: FriendListItemProps) => {
    const [showMenu, setShowMenu] = useState(false);
    const [useDefault, setUseDefault] = useState(
      !friend.mapIcon && !friend.mapColor,
    );
    const [tempMapIcon, setTempMapIcon] = useState<string>(
      friend.mapIcon || friend.defaultMapIcon || "",
    );
    const [tempMapColor, setTempMapColor] = useState<string>(
      friend.mapColor || friend.defaultMapColor || "#6366f1",
    );
    const [savedMapIcon, setSavedMapIcon] = useState<string>(
      friend.mapIcon || "",
    );
    const [savedMapColor, setSavedMapColor] = useState<string>(
      friend.mapColor || "",
    );
    const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
    const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);
    const LONG_PRESS_DURATION = 500;
    const DRAG_THRESHOLD = 10;

    const handleTouchStart = (e: React.TouchEvent) => {
      touchStartPosRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };

      longPressTimerRef.current = setTimeout(() => {
        if (touchStartPosRef.current) {
          setShowMenu(!showMenu);
          if ("vibrate" in navigator) {
            navigator.vibrate(50);
          }
        }
      }, LONG_PRESS_DURATION);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
      if (touchStartPosRef.current && longPressTimerRef.current) {
        const deltaX = Math.abs(
          e.touches[0].clientX - touchStartPosRef.current.x,
        );
        const deltaY = Math.abs(
          e.touches[0].clientY - touchStartPosRef.current.y,
        );

        if (deltaX > DRAG_THRESHOLD || deltaY > DRAG_THRESHOLD) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
          touchStartPosRef.current = null;
        }
      }
    };

    const handleTouchEnd = () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
      touchStartPosRef.current = null;
    };

    const getStatusColor = () => {
      switch (friend.status) {
        case "online":
          return "var(--success)";
        case "riding":
          return "var(--accent)";
        case "offline":
        default:
          return "var(--text-muted)";
      }
    };

    const getStatusText = () => {
      if (isPending) {
        return "Pending"; // Don't show presence info for non-friends
      }
      if (friend.status === "online") return "Online";
      if (friend.status === "riding") return "Riding";

      // For offline/invisible users, show when they were last online
      if (friend.lastOnline) {
        return `Last online ${formatDateTime(friend.lastOnline)}`;
      }
      if (friend.lastSeen) {
        return `Last seen ${formatDateTime(friend.lastSeen)}`;
      }
      return "Offline";
    };

    return (
      <div
        style={{
          padding: "var(--spacing-md)",
          background: "var(--bg-subtle)",
          border: "1px solid var(--border)",
          borderRadius: "8px",
          marginBottom: "var(--spacing-sm)",
          transition: "all 0.2s",
          position: "relative",
        }}
        onClick={() => setShowMenu(!showMenu)}
        onContextMenu={(e) => {
          e.preventDefault();
          setShowMenu(!showMenu);
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseEnter={(e) =>
          (e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)")
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.background = "var(--bg-subtle)")
        }
      >
        {/* Collapse button - positioned absolutely to top right of card */}
        {showMenu && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(false);
            }}
            style={{
              position: "absolute",
              top: "4px",
              right: "4px",
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--text-secondary)",
              fontSize: "20px",
              transition: "all 0.2s",
              zIndex: 1,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
              e.currentTarget.style.color = "var(--text-primary)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "var(--text-secondary)";
            }}
          >
            ×
          </button>
        )}

        {/* Header row with avatar, info, and View button */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
          }}
        >
          {/* Avatar */}
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              background:
                friend.mapColor ||
                friend.defaultMapColor ||
                "var(--bg-primary)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize:
                friend.mapIcon || friend.defaultMapIcon ? "22px" : "18px",
              fontWeight: "600",
              color:
                friend.mapIcon ||
                friend.defaultMapIcon ||
                friend.mapColor ||
                friend.defaultMapColor
                  ? "white"
                  : "var(--text-secondary)",
              position: "relative",
              flexShrink: 0,
            }}
          >
            {friend.mapIcon || friend.defaultMapIcon
              ? friend.mapIcon || friend.defaultMapIcon
              : getUserInitial(friend.username)}

            {/* Status indicator */}
            <div
              style={{
                position: "absolute",
                bottom: 0,
                right: 0,
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                background: getStatusColor(),
                border: "2px solid var(--bg-subtle)",
              }}
            />
          </div>

          {/* Friend info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="setting-label" style={{ lineHeight: "1.2" }}>
              {friend.username}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "var(--text-secondary)",
                marginTop: "2px",
                lineHeight: "1.3",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "8px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <span>{getStatusText()}</span>
                {/* TODO: Show what the other user shares with us */}
              </div>
              {/* Share status indicators - what we share with them */}
              {(friend.shareActivities ||
                friend.shareLocation ||
                friend.shareStatus !== false ||
                friend.shareRoutes) && (
                <span
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  {friend.shareStatus !== false && (
                    <span
                      title="Sharing online status"
                      style={{ fontSize: "15px", color: "#000", opacity: 1 }}
                    >
                      🟢
                    </span>
                  )}
                  {friend.shareLocation && (
                    <span
                      title="Sharing location"
                      style={{ fontSize: "15px", color: "#000", opacity: 1 }}
                    >
                      🗺️
                    </span>
                  )}
                  {friend.shareActivities && (
                    <span
                      title="Sharing activities"
                      style={{ fontSize: "15px", color: "#000", opacity: 1 }}
                    >
                      🚴
                    </span>
                  )}
                  {friend.shareRoutes && (
                    <span
                      title="Sharing routes"
                      style={{ fontSize: "15px", color: "#000", opacity: 1 }}
                    >
                      📍
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>

          {/* View/Done button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
            style={{
              padding: "8px 16px",
              background: showMenu ? "var(--bg-primary)" : "var(--accent)",
              border: "none",
              borderRadius: "6px",
              color: showMenu
                ? "var(--text-secondary)"
                : "var(--text-on-accent)",
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: "500",
              transition: "all 0.2s",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = "0.9";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = "1";
            }}
          >
            {showMenu ? "Done" : "View"}
          </button>
        </div>

        {/* Expanded menu */}
        {showMenu && (
          <div
            style={{
              marginTop: "12px",
              paddingTop: "12px",
              borderTop: "1px solid var(--border)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Share Online Status */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 0",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "14px",
                    color: "var(--text-primary)",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <span style={{ fontSize: "15px", color: "#000", opacity: 1 }}>
                    🟢
                  </span>
                  <span>Share Online Status</span>
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "var(--text-secondary)",
                    marginTop: "2px",
                  }}
                >
                  {isPending
                    ? "Will apply when they accept"
                    : "Let them see when you're online"}
                </div>
              </div>
              <ToggleSwitch
                checked={friend.shareStatus !== false}
                onChange={() => onToggleShareStatus(friend)}
              />
            </div>

            {/* Share Location */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 0",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "14px",
                    color: "var(--text-primary)",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <span style={{ fontSize: "15px", color: "#000", opacity: 1 }}>
                    🗺️
                  </span>
                  <span>Share Location</span>
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "var(--text-secondary)",
                    marginTop: "2px",
                  }}
                >
                  {isPending
                    ? "Will apply when they accept"
                    : "Let them see your live location"}
                </div>
              </div>
              <ToggleSwitch
                checked={!!friend.shareLocation}
                onChange={() => onToggleShareLocation(friend)}
              />
            </div>

            {/* Share Activities */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 0",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "14px",
                    color: "var(--text-primary)",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <span style={{ fontSize: "15px", color: "#000", opacity: 1 }}>
                    🚴
                  </span>
                  <span>Share Activities</span>
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "var(--text-secondary)",
                    marginTop: "2px",
                  }}
                >
                  {isPending
                    ? "Will apply when they accept"
                    : "Let them see your activity summaries"}
                </div>
              </div>
              <ToggleSwitch
                checked={!!friend.shareActivities}
                onChange={() => onToggleShareActivities(friend)}
              />
            </div>

            {/* Share Routes */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 0",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "14px",
                    color: "var(--text-primary)",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <span style={{ fontSize: "15px", color: "#000", opacity: 1 }}>
                    📍
                  </span>
                  <span>Share Routes</span>
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "var(--text-secondary)",
                    marginTop: "2px",
                  }}
                >
                  {isPending
                    ? "Will apply when they accept"
                    : "Let them see your saved routes"}
                </div>
              </div>
              <ToggleSwitch
                checked={!!friend.shareRoutes}
                onChange={() => onToggleShareRoutes(friend)}
              />
            </div>

            {/* Map Appearance (only for actual friends) */}
            {!isPending && onUpdateMapSettings && (
              <div
                style={{
                  marginTop: "16px",
                  paddingTop: "16px",
                  borderTop: "1px solid var(--border)",
                }}
              >
                <h4
                  style={{
                    fontSize: "13px",
                    color: "var(--text-secondary)",
                    marginBottom: "12px",
                    textTransform: "uppercase",
                  }}
                >
                  Map Appearance
                </h4>

                {/* Default or Customize */}
                <div style={{ marginBottom: "12px" }}>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      onClick={() => setUseDefault(true)}
                      style={{
                        flex: 1,
                        padding: "10px",
                        background: useDefault
                          ? "var(--accent)"
                          : "var(--bg-primary)",
                        border: useDefault
                          ? "2px solid var(--accent)"
                          : "1px solid var(--border)",
                        borderRadius: "6px",
                        color: useDefault
                          ? "var(--text-on-accent)"
                          : "var(--text-primary)",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      Use Default
                    </button>
                    <button
                      onClick={() => setUseDefault(false)}
                      style={{
                        flex: 1,
                        padding: "10px",
                        background: !useDefault
                          ? "var(--accent)"
                          : "var(--bg-primary)",
                        border: !useDefault
                          ? "2px solid var(--accent)"
                          : "1px solid var(--border)",
                        borderRadius: "6px",
                        color: !useDefault
                          ? "var(--text-on-accent)"
                          : "var(--text-primary)",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      Customize
                    </button>
                  </div>
                </div>

                {!useDefault && (
                  <>
                    {/* Icon Selection */}
                    <div style={{ marginBottom: "12px" }}>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "var(--text-muted)",
                          marginBottom: "8px",
                        }}
                      >
                        Map icon
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(48px, 1fr))",
                          gap: "6px",
                        }}
                      >
                        {[
                          "🚴",
                          "🏃",
                          "🏃‍♀️",
                          "🚲",
                          "⚡",
                          "🔥",
                          "🎯",
                          "⭐",
                          "💨",
                          "🚀",
                          "🏆",
                        ].map((icon) => (
                          <button
                            key={icon}
                            onClick={() => setTempMapIcon(icon)}
                            style={{
                              height: "48px",
                              borderRadius: "6px",
                              background:
                                tempMapIcon === icon
                                  ? "var(--accent)"
                                  : "var(--bg-primary)",
                              border:
                                tempMapIcon === icon
                                  ? "2px solid var(--accent)"
                                  : "1px solid var(--border)",
                              cursor: "pointer",
                              fontSize: "24px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {icon}
                          </button>
                        ))}
                        <button
                          onClick={() => setTempMapIcon("")}
                          style={{
                            height: "48px",
                            borderRadius: "6px",
                            background:
                              tempMapIcon === ""
                                ? "var(--accent)"
                                : "var(--bg-primary)",
                            border:
                              tempMapIcon === ""
                                ? "2px solid var(--accent)"
                                : "1px solid var(--border)",
                            cursor: "pointer",
                            fontSize: "10px",
                            color: "var(--text-secondary)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          Initials
                        </button>
                      </div>
                    </div>

                    {/* Color Selection */}
                    <div style={{ marginBottom: "12px" }}>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "var(--text-muted)",
                          marginBottom: "8px",
                        }}
                      >
                        Map color
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(40px, 1fr))",
                          gap: "6px",
                        }}
                      >
                        {[
                          "#6366f1", // Indigo
                          "#8b5cf6", // Purple
                          "#ec4899", // Pink
                          "#f43f5e", // Rose
                          "#f97316", // Orange
                          "#eab308", // Yellow
                          "#22c55e", // Green
                          "#14b8a6", // Teal
                          "#06b6d4", // Cyan
                          "#3b82f6", // Blue
                        ].map((color) => (
                          <button
                            key={color}
                            onClick={() => setTempMapColor(color)}
                            style={{
                              height: "40px",
                              borderRadius: "6px",
                              background: color,
                              border:
                                tempMapColor === color
                                  ? "3px solid white"
                                  : "none",
                              cursor: "pointer",
                              boxShadow:
                                tempMapColor === color
                                  ? "0 0 0 2px var(--accent)"
                                  : "none",
                            }}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Preview */}
                    <div style={{ marginBottom: "12px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <div
                          style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "50%",
                            background:
                              tempMapColor ||
                              friend.defaultMapColor ||
                              "#6366f1",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize:
                              tempMapIcon || friend.defaultMapIcon
                                ? "20px"
                                : "16px",
                            fontWeight: "600",
                            color: "white",
                          }}
                        >
                          {tempMapIcon ||
                            friend.defaultMapIcon ||
                            getUserInitial(friend.username)}
                        </div>
                        <span
                          style={{
                            fontSize: "12px",
                            color: "var(--text-secondary)",
                          }}
                        >
                          Preview
                        </span>
                      </div>
                    </div>
                  </>
                )}

                {/* Save Button */}
                {((useDefault &&
                  (savedMapIcon !== "" || savedMapColor !== "")) ||
                  (!useDefault &&
                    (tempMapIcon !== savedMapIcon ||
                      tempMapColor !== savedMapColor))) && (
                  <button
                    onClick={() => {
                      if (useDefault) {
                        onUpdateMapSettings(friend, "", "");
                        setSavedMapIcon("");
                        setSavedMapColor("");
                      } else {
                        onUpdateMapSettings(friend, tempMapIcon, tempMapColor);
                        setSavedMapIcon(tempMapIcon);
                        setSavedMapColor(tempMapColor);
                      }
                    }}
                    style={{
                      width: "100%",
                      padding: "8px",
                      background: "var(--accent)",
                      border: "none",
                      borderRadius: "4px",
                      color: "var(--text-on-accent)",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    Save
                  </button>
                )}
              </div>
            )}

            {/* Remove/Cancel button */}
            <button
              onClick={() => onRemove(friend)}
              style={{
                width: "100%",
                marginTop: "8px",
                padding: "10px",
                background: "transparent",
                border: "1px solid var(--danger)",
                borderRadius: "6px",
                color: "var(--danger)",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--danger)";
                e.currentTarget.style.color = "white";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "var(--danger)";
              }}
            >
              {isPending ? "Cancel Request" : "Remove Friend"}
            </button>
          </div>
        )}
      </div>
    );
  },
);
